import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import { TimeSlots } from "@/components/appointmentRequest/timeSlots";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "@/components/state/store";
import getDay from "date-fns/getDay";
import { Divider, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import de from "date-fns/locale/de";
import { api } from "@/helper/api";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import TimeSlotPickConfirmation from "@/components/appointmentRequest/timeSlotPickConfirmation";
import { useRemainingManagerBookings } from "@/components/appointmentRequest/useRemainingManagerBookings";
import styles from "@/components/appointmentRequest/appointmentRequestSlots.module.css";
import dayjs from "dayjs";
import inlineCalendarStyles from "@/components/dateTime/genericInlineCalendar.module.css";

registerLocale("de", de);

interface Props {
  subdomain?: string;
}

export function AppointmentRequestSlots({ subdomain }: Props) {
  const { t, i18n } = useTranslation();
  const [startDate, setStartDate] = useState<Date | undefined | null>(null);
  const [daySet, setDaySet] = useState<any>([]);
  const [errorKind, setErrorKind] = useState<
    "allSlotsTaken" | "allSlotsInPast" | "generic" | null
  >(null);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [responseConfirmationInformation, setResponseConfirmationInformation] =
    useState<any>();
  const [searchParams] = useSearchParams();
  const isManagerBooking =
    subdomain === "manage" && searchParams.get("booking") === "workers";
  usePageHeading(
    t(isManagerBooking ? "assign_worker_appointment" : "choose_date_time"),
  );
  const [bookingRound, setBookingRound] = useState(0);
  const companySubjectId = useSelector(
    (state: RootState) => state.parent.subjectId,
  );

  const navigate = useNavigate();
  const isSlotAvailabilityHint =
    errorKind === "allSlotsTaken" || errorKind === "allSlotsInPast";
  const { handleError, errorModal } = useErrorModal(
    isSlotAvailabilityHint ? t("hint") : t("error_occurred"),
    {
      onClose: () => {
        setErrorKind(null);
        if (isSlotAvailabilityHint) {
          navigate("/appointmentrequestopen");
        }
      },
    },
  );
  const calendarLocale = i18n.language === "de" ? "de" : undefined;

  const isWeekday = (date: any) => {
    const day = getDay(date);
    return day !== 0 && day !== 6;
  };

  const today = dayjs().startOf("day");

  const isSelectableDate = (date: Date) =>
    isWeekday(date) && !dayjs(date).isBefore(today, "day");

  const { appointmentrequestId } = useParams() as {
    appointmentrequestId: string;
  };

  const remainingBookings = useRemainingManagerBookings(
    appointmentrequestId,
    companySubjectId,
    isManagerBooking && showConfirmation,
  );

  const handleShowConfirmationAfterPick = () => {
    setShowConfirmation(true);
  };

  const handleGetResponseAfterPick = (data: any) => {
    setResponseConfirmationInformation(data);
  };

  const clearSelectedSlots = () => {
    setDaySet([]);
    setStartDate(null);
  };

  useEffect(() => {
    let disposed = false;
    clearSelectedSlots();
    setShowConfirmation(false);
    if (appointmentrequestId !== "") {
      api.genericApiRequest({
        method: "get",
        entity: "appointmentRequest",
        entityId: appointmentrequestId,
        additionalRouteParts: isManagerBooking
          ? { free_slots: "/" }
          : undefined,
        successHandler: (res: any) => {
          if (disposed) return;
          const managerBookingContext =
            res?.data?.manager_booking_context ?? null;
          const availableDays = isManagerBooking
            ? managerBookingContext?.available_day_set || []
            : res?.data?.available_day_set || [];
          const selectableDays = availableDays
            .filter((date: string) => !dayjs(date).isBefore(today, "day"))
            .sort();
          const hasAvailableDays = availableDays.length > 0;
          const allAvailableDaysAreInPast =
            hasAvailableDays && selectableDays.length === 0;

          if (selectableDays.length) {
            const transformedDays = selectableDays.map((date: any) =>
              dayjs(date)
                .hour(12)
                .minute(0)
                .second(38)
                .millisecond(941)
                .format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
            );

            setDaySet(transformedDays);
            setStartDate(dayjs(selectableDays[0]).toDate());
            setErrorKind(null);
          } else if (allAvailableDaysAreInPast) {
            clearSelectedSlots();
            handleError(t("error_all_slots_taken"));
            setErrorKind("allSlotsInPast");
          } else {
            clearSelectedSlots();
            handleError(t("error_all_slots_taken"));
            setErrorKind("allSlotsTaken");
          }
        },
        failHandler: (error: any) => {
          if (disposed) return;
          handleError(error);
          setErrorKind("generic");
        },
        parametersToRender: {
          depth: "0",
          additionalUrlParameters: isManagerBooking
            ? { include_manager_booking_context: true, limit: 1 }
            : {},
        },
      });
    }
    return () => {
      disposed = true;
    };
  }, [appointmentrequestId, isManagerBooking, companySubjectId, bookingRound]);

  const intervals = daySet.map((day: any) => ({
    start: dayjs(day).subtract(1, "day").toDate(),
    end: dayjs(day).toDate(),
  }));

  return (
    <>
      {!showConfirmation ? (
        <Paper
          className={styles.timeSlotsWrapper}
          style={{ height: startDate ? "" : styles.fullHeight }}
        >
          {isManagerBooking && (
            <Typography>{t("worker_assignment_instructions")}</Typography>
          )}
          <div
            className={inlineCalendarStyles.surface}
            role={isManagerBooking ? "group" : undefined}
            aria-labelledby={
              isManagerBooking ? "worker-booking-date-label" : undefined
            }
          >
            {isManagerBooking && (
              <Typography
                component="h3"
                variant="subtitle1"
                id="worker-booking-date-label"
              >
                {t("select_appointment_date")}
              </Typography>
            )}
            <DatePicker
              locale={calendarLocale}
              calendarStartDay={1}
              inline
              disabledKeyboardNavigation
              filterDate={isSelectableDate}
              includeDateIntervals={intervals}
              minDate={today.toDate()}
              selected={startDate}
              calendarClassName={[
                inlineCalendarStyles.calendar,
                inlineCalendarStyles.appointmentRequestCalendar,
              ].join(" ")}
              onChange={(date: any) => {
                if (date && isSelectableDate(date)) {
                  setStartDate(date);
                }
              }}
            />
          </div>
          {startDate && (
            <>
              <Divider />
              <TimeSlots
                key={`${appointmentrequestId}-${companySubjectId}-${isManagerBooking}-${bookingRound}`}
                selectedDay={startDate}
                handleGetResponseAftetPick={handleGetResponseAfterPick}
                handleShowConfirmationAfterPick={
                  handleShowConfirmationAfterPick
                }
                workerBooking={isManagerBooking}
              />
            </>
          )}
        </Paper>
      ) : (
        <TimeSlotPickConfirmation
          appointmentInformation={responseConfirmationInformation}
          managerBooking={isManagerBooking}
          repeatAssignmentState={remainingBookings.state}
          onRetryAssignmentCheck={remainingBookings.retry}
          onAssignAnother={() => {
            if (remainingBookings.state !== "available") return;
            clearSelectedSlots();
            setResponseConfirmationInformation(undefined);
            setShowConfirmation(false);
            setBookingRound((round) => round + 1);
          }}
        />
      )}
      {errorModal}
    </>
  );
}
